import React, { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Popover,
  Button,
  IconButton,
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { DateRangePicker } from 'react-date-range';
import { addDays, format, isSameDay } from 'date-fns';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  CartesianGrid,
  ReferenceLine,
  Cell,
  LabelList,
} from 'recharts';

const START_HOUR = 8;
const END_HOUR = 24;
const START_MINUTE = START_HOUR * 60;
const END_MINUTE = END_HOUR * 60;
const MAX_Y_HOURS = 10;
const THRESHOLD_HOURS = 9;

const TIME_IN_COLOR = '#86efac';
const TIME_IN_STROKE = '#22c55e';
const CHECKOUT_COLOR = '#FEE2E2';
const CHECKOUT_STROKE = '#D32F2F';

const getTimeInDisplayTimezone = (dateValue) => {
  let d = null;
  if (dateValue instanceof Date) {
    d = dateValue;
  } else if (typeof dateValue === 'string') {
    const trimmed = dateValue.trim();
    const utcString = trimmed.endsWith('Z') ? trimmed : `${trimmed}Z`;
    d = new Date(utcString);
  } else {
    d = new Date(dateValue);
  }

  if (!d || Number.isNaN(d.getTime())) return { hours: 0, minutes: 0 };

  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  });
  const parts = formatter.formatToParts(d);
  const hours = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
  const minutes = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);
  return { hours, minutes };
};

const minutesToHours = (minutes) => Math.round((minutes / 60) * 100) / 100;

const formatDurationLabel = (totalMinutes) => {
  const hours = Math.floor(totalMinutes / 60);
  const mins = totalMinutes % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const buildDaySegments = (events, isToday) => {
  const dayEvents = [...events].sort(
    (a, b) => new Date(a.created_at || a.timestamp) - new Date(b.created_at || b.timestamp)
  );

  const segments = [];
  let lastCheckOutMinute = null;
  let lastCheckInMinute = null;

  const clampMinute = (minute) => Math.max(START_MINUTE, Math.min(END_MINUTE, minute));

  const pushSegment = (type, startMinute, endMinute) => {
    const start = clampMinute(startMinute);
    const end = clampMinute(endMinute);
    if (end <= start) return;
    segments.push({
      type,
      minutes: end - start,
      hours: minutesToHours(end - start),
    });
  };

  dayEvents.forEach((event) => {
    const { hours, minutes } = getTimeInDisplayTimezone(event.created_at || event.timestamp);
    const eventMinute = hours * 60 + minutes;
    const clampedEventMinute = Math.max(START_MINUTE, Math.min(END_MINUTE, eventMinute));

    if (event.action === 'check_in') {
      if (lastCheckInMinute !== null && lastCheckOutMinute === null) {
        pushSegment('present', lastCheckInMinute, clampedEventMinute);
      } else if (lastCheckOutMinute !== null) {
        pushSegment('away', lastCheckOutMinute, clampedEventMinute);
      }
      lastCheckInMinute = eventMinute;
      lastCheckOutMinute = null;
    } else if (event.action === 'check_out') {
      if (lastCheckOutMinute !== null && lastCheckInMinute === null) {
        pushSegment('away', lastCheckOutMinute, clampedEventMinute);
      }
      if (lastCheckInMinute !== null) {
        pushSegment('present', lastCheckInMinute, clampedEventMinute);
      }
      lastCheckOutMinute = eventMinute;
      lastCheckInMinute = null;
    }
  });

  if (lastCheckInMinute !== null) {
    let endMinute = END_MINUTE;
    if (isToday) {
      const now = getTimeInDisplayTimezone(new Date());
      endMinute = Math.max(START_MINUTE, Math.min(END_MINUTE, now.hours * 60 + now.minutes));
    }
    pushSegment('present', lastCheckInMinute, endMinute);
  }

  const presenceMinutes = segments
    .filter((segment) => segment.type === 'present')
    .reduce((sum, segment) => sum + segment.minutes, 0);

  const awayMinutes = segments
    .filter((segment) => segment.type === 'away')
    .reduce((sum, segment) => sum + segment.minutes, 0);

  return { segments, presenceMinutes, awayMinutes };
};

export const buildHistogramChartData = (events, rangeStart, rangeEnd) => {
  if (!rangeStart || !rangeEnd) return { rows: [], maxSegments: 0 };

  const dayCount = Math.ceil((rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const rows = [];
  let maxSegments = 0;

  for (let index = 0; index < dayCount; index += 1) {
    const currentDate = addDays(rangeStart, index);
    const eventsForDate = events.filter((event) =>
      isSameDay(new Date(event.timestamp || event.created_at), currentDate)
    );

    const row = {
      dateKey: format(currentDate, 'yyyy-MM-dd'),
      displayDate: format(currentDate, 'MMM dd'),
      displayDay: format(currentDate, 'EEE'),
      displayMonthDay: format(currentDate, 'MMM dd'),
      fullDate: format(currentDate, 'EEE, MMM dd, yyyy'),
      hasData: false,
      totalLabel: null,
      presenceHours: 0,
      segmentMeta: [],
    };

    if (eventsForDate.length > 0) {
      const isToday = isSameDay(currentDate, new Date());
      const { segments, presenceMinutes, awayMinutes } = buildDaySegments(eventsForDate, isToday);
      if (segments.length > 0) {
        maxSegments = Math.max(maxSegments, segments.length);
        row.hasData = true;
        row.totalLabel = formatDurationLabel(presenceMinutes);
        row.inPremisesLabel = formatDurationLabel(presenceMinutes);
        row.outPremisesLabel = formatDurationLabel(awayMinutes);
        row.presenceHours = minutesToHours(presenceMinutes);
        row.segmentMeta = segments.map((segment) => segment.type);
        row.segmentDetails = segments.map((segment) => ({
          type: segment.type,
          label: formatDurationLabel(segment.minutes),
        }));
        segments.forEach((segment, segmentIndex) => {
          row[`seg${segmentIndex}`] = segment.hours;
        });
      }
    }

    rows.push(row);
  }

  rows.forEach((row) => {
    for (let segmentIndex = 0; segmentIndex < maxSegments; segmentIndex += 1) {
      if (row[`seg${segmentIndex}`] == null) {
        row[`seg${segmentIndex}`] = 0;
      }
    }
  });

  return { rows, maxSegments };
};

const HistogramTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;

  const row = payload[0]?.payload;
  if (!row || !row.hasData) return null;

  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        p: 1.25,
        minWidth: 160,
        maxWidth: 220,
        pointerEvents: 'none',
      }}
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#334155', mb: 0.75 }}>
        {row.fullDate}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: TIME_IN_STROKE, fontWeight: 600, lineHeight: 1.6 }}>
        In premises: {row.inPremisesLabel || row.totalLabel}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: CHECKOUT_STROKE, fontWeight: 600, lineHeight: 1.6 }}>
        Out premises: {row.outPremisesLabel || '0m'}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontWeight: 500, mt: 0.75, fontSize: '0.65rem' }}>
        Click bar for full details
      </Typography>
    </Box>
  );
};

const DayDetailsPopover = ({ open, anchorPosition, row, onClose }) => {
  if (!row) return null;

  const segments = row.segmentDetails || [];

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            minWidth: 200,
            maxWidth: 260,
          },
        },
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, color: '#334155', mb: 1 }}>
          {row.fullDate}
        </Typography>
        <Box
          sx={{
            maxHeight: 220,
            overflowY: 'auto',
            pr: 0.5,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#cbd5e1', borderRadius: 3 },
          }}
        >
          {segments.map((segment, index) => (
            <Typography
              key={`${row.dateKey}-detail-${index}`}
              variant="caption"
              sx={{
                display: 'block',
                color: segment.type === 'present' ? TIME_IN_STROKE : CHECKOUT_STROKE,
                fontWeight: 600,
                lineHeight: 1.7,
              }}
            >
              {segment.type === 'present' ? 'Time In' : 'Time Out'}: {segment.label}
            </Typography>
          ))}
        </Box>
        <Stack spacing={0.25} sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ color: TIME_IN_STROKE, fontWeight: 600 }}>
            In premises: {row.inPremisesLabel || row.totalLabel}
          </Typography>
          <Typography variant="caption" sx={{ color: CHECKOUT_STROKE, fontWeight: 600 }}>
            Out premises: {row.outPremisesLabel || '0m'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700 }}>
            Total presence: {row.totalLabel}
          </Typography>
        </Stack>
      </Box>
    </Popover>
  );
};

const TotalLabel = ({ x, y, width, value, payload }) => {
  if (!payload?.hasData || value == null || x == null || y == null || width == null) return null;
  return (
    <text
      x={x + width / 2}
      y={y - 8}
      fill="#64748b"
      textAnchor="middle"
      fontSize={11}
      fontWeight={600}
    >
      {`Total: ${value}`}
    </text>
  );
};

/** Two-line X tick: weekday on top, month+date below (no rotation). */
const DualLineXAxisTick = ({ x, y, payload, fontSize = 11, labelByDisplayDate }) => {
  if (x == null || y == null) return null;
  const labels = labelByDisplayDate?.get(payload?.value) || null;
  const day = labels?.day || payload?.displayDay;
  const dateLabel = labels?.date || payload?.displayMonthDay;
  if (!day) return null;
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill="#94a3b8" fontWeight={500} fontSize={fontSize}>
        <tspan x={0} dy={12}>{day}</tspan>
        {dateLabel ? (
          <tspan x={0} dy={13} fill="#667085" fontWeight={600} fontSize={Math.max(9, fontSize - 1)}>
            {dateLabel}
          </tspan>
        ) : null}
      </text>
    </g>
  );
};

const getChartLayout = (periodDays) => {
  if (periodDays >= 30) {
    return {
      barCategoryGap: '8%',
      xAxisHeight: 42,
      xAxisInterval: 'preserveStartEnd',
      xAxisMinTickGap: 18,
      xAxisFontSize: 10,
      bottomMargin: 16,
      showTotals: false,
    };
  }
  if (periodDays >= 15) {
    return {
      barCategoryGap: '14%',
      xAxisHeight: 42,
      xAxisInterval: 'preserveStartEnd',
      xAxisMinTickGap: 20,
      xAxisFontSize: 10,
      bottomMargin: 14,
      showTotals: false,
    };
  }
  return {
    barCategoryGap: '20%',
    xAxisHeight: 44,
    xAxisInterval: 0,
    xAxisMinTickGap: 22,
    xAxisFontSize: 11,
    bottomMargin: 12,
    showTotals: true,
  };
};

export const AttendancePresenceGraph = ({
  events = [],
  dateRangeStart,
  dateRangeEnd,
  height = 460,
  periodDays = 7,
  onPeriodChange,
  onCustomDateRangeApply,
}) => {
  const today = useMemo(() => {
    const value = new Date();
    value.setHours(0, 0, 0, 0);
    return value;
  }, []);

  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [draftDateRange, setDraftDateRange] = useState([
    {
      startDate: dateRangeStart || addDays(today, -6),
      endDate: dateRangeEnd || today,
      key: 'selection',
    },
  ]);

  const datePickerOpen = Boolean(datePickerAnchor);

  const handlePeriodChange = (_, nextPeriod) => {
    if (!nextPeriod) return;
    onPeriodChange?.(nextPeriod);
  };

  const handleOpenDatePicker = (event) => {
    const startDate = dateRangeStart ? new Date(dateRangeStart) : addDays(today, -6);
    const endDate = dateRangeEnd ? new Date(dateRangeEnd) : today;
    setDraftDateRange([{ startDate, endDate, key: 'selection' }]);
    setDatePickerAnchor(event.currentTarget);
  };

  const handleCloseDatePicker = () => {
    setDatePickerAnchor(null);
  };

  const handleApplyDateRange = () => {
    const selection = draftDateRange[0];
    if (!selection?.startDate || !selection?.endDate) return;
    onCustomDateRangeApply?.({
      startDate: selection.startDate,
      endDate: selection.endDate,
    });
    handleCloseDatePicker();
  };

  const dateRangeLabel = useMemo(() => {
    if (!dateRangeStart || !dateRangeEnd) return 'Select dates';
    if (isSameDay(dateRangeStart, dateRangeEnd)) {
      return format(dateRangeStart, 'MMM dd, yyyy');
    }
    return `${format(dateRangeStart, 'MMM dd')} - ${format(dateRangeEnd, 'MMM dd')}`;
  }, [dateRangeStart, dateRangeEnd]);

  const rangeDayCount = useMemo(() => {
    if (!dateRangeStart || !dateRangeEnd) return periodDays || 7;
    return Math.ceil((dateRangeEnd.getTime() - dateRangeStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  }, [dateRangeStart, dateRangeEnd, periodDays]);

  const layoutPeriodDays = periodDays ?? rangeDayCount;

  const { rows, maxSegments } = useMemo(
    () => buildHistogramChartData(events, dateRangeStart, dateRangeEnd),
    [events, dateRangeStart, dateRangeEnd]
  );

  const xAxisLabelByDisplayDate = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      map.set(row.displayDate, { day: row.displayDay, date: row.displayMonthDay });
    });
    return map;
  }, [rows]);

  const segmentIndexes = useMemo(
    () => Array.from({ length: maxSegments }, (_, index) => index),
    [maxSegments]
  );

  const hasAnyData = rows.some((row) => row.hasData);
  const chartLayout = useMemo(() => getChartLayout(layoutPeriodDays), [layoutPeriodDays]);
  const topSegmentIndex = maxSegments - 1;

  const [detailPopover, setDetailPopover] = useState({
    open: false,
    anchorPosition: null,
    row: null,
  });

  const handleBarClick = useCallback((barData, _index, event) => {
    event?.stopPropagation?.();
    event?.preventDefault?.();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const row = barData?.payload ?? barData;
    if (!row?.hasData) return;

    setDetailPopover({
      open: true,
      anchorPosition: { top: event.clientY, left: event.clientX },
      row,
    });
  }, []);

  const handleCloseDetailPopover = useCallback(() => {
    setDetailPopover({ open: false, anchorPosition: null, row: null });
  }, []);

  useEffect(() => {
    handleCloseDetailPopover();
  }, [periodDays, dateRangeStart, dateRangeEnd, handleCloseDetailPopover]);

  const chartHeader = (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      spacing={1.5}
      justifyContent="space-between"
      alignItems={{ xs: 'stretch', sm: 'center' }}
      sx={{ mb: 1.5 }}
    >
      <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 12, height: 12, bgcolor: TIME_IN_COLOR, border: `1px solid ${TIME_IN_STROKE}`, borderRadius: 0.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>In Premises</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 12, height: 12, bgcolor: CHECKOUT_COLOR, border: `1px solid ${CHECKOUT_STROKE}`, borderRadius: 0.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>Out Premises</Typography>
        </Stack>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box sx={{ width: 18, height: 0, borderTop: '2px dashed #ef4444' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b' }}>9-Hour Threshold</Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Box
          onClick={handleOpenDatePicker}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            px: 1.25,
            py: 0.55,
            bgcolor: 'rgba(64, 81, 138, 0.04)',
            cursor: 'pointer',
            minWidth: { xs: '100%', sm: 170 },
            '&:hover': {
              borderColor: '#40518A',
              bgcolor: 'rgba(64, 81, 138, 0.08)',
            },
          }}
        >
          <CalendarToday sx={{ fontSize: 16, color: 'action.active' }} />
          <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', sm: '0.82rem' }, fontWeight: 500, color: '#374151' }}>
            {dateRangeLabel}
          </Typography>
          <IconButton size="small" sx={{ ml: 'auto', p: 0.25 }} onClick={(event) => { event.stopPropagation(); handleOpenDatePicker(event); }}>
            <CalendarToday sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>

        <ToggleButtonGroup
          value={periodDays}
          exclusive
          onChange={handlePeriodChange}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              flex: { xs: 1, sm: 'initial' },
              px: { xs: 1, sm: 1.5 },
              py: 0.5,
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              textTransform: 'none',
              borderColor: 'divider',
              '&.Mui-selected': {
                backgroundColor: '#40518A',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#364775',
                },
              },
            },
          }}
        >
          <ToggleButton value={7}>7 Days</ToggleButton>
          <ToggleButton value={15}>15 Days</ToggleButton>
          <ToggleButton value={30}>30 Days</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Popover
        open={datePickerOpen}
        anchorEl={datePickerAnchor}
        onClose={handleCloseDatePicker}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box>
          <DateRangePicker
            ranges={draftDateRange}
            onChange={(ranges) => setDraftDateRange([ranges.selection])}
            moveRangeOnFirstSelection={false}
            months={1}
            direction="horizontal"
            rangeColors={['#40518A']}
            staticRanges={[]}
            inputRanges={[]}
            maxDate={today}
            showDateDisplay
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', p: 2, pt: 0 }}>
            <Button onClick={handleCloseDatePicker} variant="outlined" size="small">
              Cancel
            </Button>
            <Button
              onClick={handleApplyDateRange}
              variant="contained"
              size="small"
              sx={{ bgcolor: '#40518A', '&:hover': { bgcolor: '#364775' } }}
            >
              Apply
            </Button>
          </Box>
        </Box>
      </Popover>
    </Stack>
  );

  if (!hasAnyData) {
    return (
      <Box sx={{ width: '100%' }}>
        {chartHeader}
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            No attendance records found for this date range
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {chartHeader}

      <DayDetailsPopover
        open={detailPopover.open}
        anchorPosition={detailPopover.anchorPosition}
        row={detailPopover.row}
        onClose={handleCloseDetailPopover}
      />

      <Box
        sx={{
          '& .recharts-wrapper': { outline: 'none !important' },
          '& .recharts-surface': { outline: 'none !important' },
          '& .recharts-responsive-container': { outline: 'none !important' },
          '& svg': { outline: 'none !important' },
          '& svg:focus': { outline: 'none !important' },
          '& svg:focus-visible': { outline: 'none !important' },
          '& *:focus': { outline: 'none !important' },
          '& .recharts-tooltip-cursor': { stroke: 'none !important', fill: 'rgba(148, 163, 184, 0.08) !important' },
        }}
        onMouseDown={(event) => event.preventDefault()}
      >
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={rows}
          margin={{ top: 28, right: 10, left: -8, bottom: chartLayout.bottomMargin }}
          barGap={0}
          barCategoryGap={chartLayout.barCategoryGap}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#d9e0ee" />
          <XAxis
            dataKey="displayDate"
            tick={(tickProps) => (
              <DualLineXAxisTick
                {...tickProps}
                fontSize={chartLayout.xAxisFontSize}
                labelByDisplayDate={xAxisLabelByDisplayDate}
              />
            )}
            axisLine={{ stroke: '#d9e0ee' }}
            tickLine={false}
            interval={chartLayout.xAxisInterval}
            minTickGap={chartLayout.xAxisMinTickGap}
            height={chartLayout.xAxisHeight}
          />
          <YAxis
            domain={[0, 11.2]}
            ticks={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10]}
            allowDataOverflow={true}
            tickFormatter={(value) => `${value}h`}
            tick={{ fontSize: 11, fill: '#667085', fontWeight: 500 }}
            axisLine={false}
            tickLine={false}
            width={42}
          />
          <RechartsTooltip
            content={<HistogramTooltip />}
            cursor={false}
          />
          <ReferenceLine
            y={THRESHOLD_HOURS}
            stroke="#ef4444"
            strokeDasharray="5 5"
            strokeWidth={1.5}
            label={{
              position: 'right',
              fill: '#ef4444',
              fontSize: 11,
              fontWeight: 600,
            }}
          />
          {segmentIndexes.map((segmentIndex) => (
            <Bar
              key={`segment-${segmentIndex}`}
              dataKey={`seg${segmentIndex}`}
              stackId="presence"
              stroke={segmentIndex === topSegmentIndex ? '#cbd5e1' : 'none'}
              strokeWidth={segmentIndex === topSegmentIndex ? 0.5 : 0}
              radius={segmentIndex === topSegmentIndex ? [6, 6, 0, 0] : [0, 0, 0, 0]}
              isAnimationActive={false}
              activeBar={false}
              cursor="pointer"
              onClick={handleBarClick}
            >
              {rows.map((row) => (
                <Cell
                  key={`${row.dateKey}-${segmentIndex}`}
                  fill={
                    !row.hasData || !row.segmentMeta?.[segmentIndex]
                      ? 'transparent'
                      : row.segmentMeta[segmentIndex] === 'present'
                        ? TIME_IN_COLOR
                        : CHECKOUT_COLOR
                  }
                  stroke={
                    !row.hasData || !row.segmentMeta?.[segmentIndex]
                      ? 'transparent'
                      : row.segmentMeta[segmentIndex] === 'present'
                        ? TIME_IN_STROKE
                        : CHECKOUT_STROKE
                  }
                  strokeWidth={0.5}
                />
              ))}
              {segmentIndex === topSegmentIndex && chartLayout.showTotals && (
                <LabelList dataKey="totalLabel" content={<TotalLabel />} />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default AttendancePresenceGraph;
